<template>
  <div class="organization-page" data-testid="organization-page">
    <div class="organization-page-title-container">
      <PageTitle title="Organization" />
    </div>
    <OrganizationHorizontalMenu
      :menuItems="menuItems"
      :initialTab="currentTab"
      :count_of_active_employees="sidebarCountStore.employees"
      :count_of_active_positions="sidebarCountStore.positions"
      :count_of_active_roles="sidebarCountStore.roles"
      :count_of_active_responsibilities="sidebarCountStore.responsibilities"
      :count_of_active_procedures="sidebarCountStore.procedures"
      @tab-selected="handleTabChange"
    />
    <div class="tab-content" v-if="showTabContent">
      <OrganizationDashboardTab
        v-if="currentTab === 'Dashboard'"
        :guideData="dashboardGuideData"
        :learn_about_positions_url="learn_about_positions_url"
        :learn_about_roles_url="learn_about_roles_url"
        :learn_about_responsibilities_url="learn_about_responsibilities_url"
        :learn_about_employees_url="learn_about_employees_url"
        @guide-closed="handleGuideClose"
        @change-tab="handleTabChange"
        @drilldown="handleDrilldown"
      />
      <OrganizationEmployeesTab
      class="employees-tab-container"
        v-if="currentTab === 'Employees'"
        :employees="employeesData"
        :activeEmployeesWithNoaccess="activeEmployeesWithNoaccess"
        :description="employeesDescription"
        :actionLabel="employeesActionLabel"
        @drilldown="handleDrilldown"
      />
      <OrganizationPositionsTab
      class="positions-tab-container"
        v-if="currentTab === 'Positions'"
        :positions="positions"
        :pagination="positionsPagination"
        :loading="positionsLoading"
        :searchText="positionsSearchText"
        :appliedFilters="positionsAppliedFilters"
        :backendPagination="positionsBackendPagination"
        :title="positionsTitle"
        :actionLabel="positionsActionLabel"
        :hideTopSection="positionsHideTopSection"
        :hideActiveNo="positionsHideActiveNo"
        :activeRecordsCount="positionsActiveRecordsCount"
        @drilldown="handleDrilldown"
      />
      <OrganizationRolesTab 
      class="role-tab-container"
        v-if="currentTab === 'Roles'" 
        :guideData="rolesGuideData"
        @drilldown="handleDrilldown"
        @guide-close="handleGuideCloseForRoles"
        :useNewBadge="true"
      />
      <OrganizationResponsibilitiesTab
      class="responsibilities-tab-container"
        v-if="currentTab === 'Responsibilities'"
        :resCount="responsibilitiesResCount"
        :responsibilities="responsibilities"
        :allResponsibilities="allResponsibilities"
        :pagination="responsibilitiesPagination"
        :loading="responsibilitiesLoading"
        :chatterData="responsibilitiesChatterData"
        :totalChatterData="responsibilitiesTotalChatterData"
        :searchText="responsibilitiesSearchText"
        :appliedFilters="responsibilitiesAppliedFilters"
        :filterGroups="responsibilitiesFilterGroups"
        :statusOptions="responsibilitiesStatusOptions"
        :showSearchBar="responsibilitiesShowSearchBar"
        :totalCount="responsibilitiesTotalCount"
      />
    </div>
    <!-- Employee Detail shown when drilldown is triggered -->
    <div class="drilldown-content" v-if="!showTabContent && drilldownData && drilldownData.employeeId">
      <EmployeeDetail 
        :key="`employee-${drilldownData.employeeId}-${drilldownData.tab}`"
        :employeeId="drilldownData.employeeId" 
        @backToList="handleBackToList" 
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import OrganizationHorizontalMenu from "@components/OrganizationHorizontalMenu/OrganizationHorizontalMenu.vue";
import OrganizationDashboardTab from "@components/OrganizationDashboardTab/OrganizationDashboardTab.vue";
import OrganizationEmployeesTab from "@components/OrganizationEmployeesTab/OrganizationEmployeesTab.vue";
import OrganizationPositionsTab from "@components/OrganizationPositionsTab/OrganizationPositionsTab.vue";
import OrganizationRolesTab from "@components/OrganizationRolesTab/OrganizationRolesTab.vue";
import OrganizationResponsibilitiesTab from "@components/OrganizationResponsibilitiesTab/OrganizationResponsibilitiesTab.vue";
import EmployeeDetail from "@components/EmployeeDetail/EmployeeDetail.vue";
// Import SVG images as paths
import Home from "@components/assets/icons/Home.svg";
import Employee from "@components/assets/icons/Employee.svg";
import Position from "@components/assets/icons/Position.svg";
import Role from "@components/assets/icons/Role.svg";
import Responsibility from "@components/assets/icons/Responsibility.svg";
import Procedures from "@components/assets/icons/puzzle-piece.svg";
import PageTitle from "@components/common/PageTitle.vue";
import { useSidebarCountStore } from "@/store/pinia/sidebarCount";
import { useEmployees } from "@/composables/employees/useEmployees";
import { useEmployeesOrganizationTableViewState } from "@components/EmployeesOrganizationTable/composables/useEmployeesOrganizationTableViewState";
import Knock, { KnockGuideClient } from "@knocklabs/client";
import configService from "../../account/src/services/config";
import { getListofEmployeesV2 } from "@/api/employeeAPI";

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const route = proxy?.$route;
const router = proxy?.$router;
const sidebarCountStore = useSidebarCountStore();

const employees = useEmployees();
const employeesViewState = useEmployeesOrganizationTableViewState();

const employeesData = ref([]);
const activeEmployeesWithNoaccess = ref(0);
const employeesDescription = ref("To effectively manage Readiness and Performance employees need to have access to their stuff");
const employeesActionLabel = ref("Apply Filter");
const validTabs = [
  "Dashboard",
  "Employees",
  "Positions",
  "Roles",
  "Responsibilities",
  "Procedures",
];

const getTabFromRoute = () => {
  const tabParam = route?.params?.tab;
  if (tabParam) {
    // Find matching tab (case-insensitive)
    const matchedTab = validTabs.find(
      (tab) => tab.toLowerCase() === tabParam.toLowerCase()
    );
    return matchedTab || "Dashboard";
  }
  return "Dashboard";
};
const props = defineProps({
  guide_primary_text: {
    type: String,
    default: "Welcome to exIQtive, the Readiness Management Platform",
  },
  guide_secondary_text: {
    type: String,
    default:
      "As an owner or co-owner of an account you can manage the organization consisting of Positions, Roles, Responsibilities and Employees",
  },
  guideData: {
    type: Object,
    default: null,
  },
  has_positions: {
    type: Boolean,
    default: false,
  },
  has_roles: {
    type: Boolean,
    default: false,
  },
  has_responsibilities: {
    type: Boolean,
    default: false,
  },
  has_employees: {
    type: Boolean,
    default: false,
  },
  learn_about_positions_url: {
    type: String,
    default: null,
  },
  learn_about_roles_url: {
    type: String,
    default: null,
  },
  learn_about_responsibilities_url: {
    type: String,
    default: null,
  },
  learn_about_employees_url: {
    type: String,
    default: null,
  },
  // Employees tab props
  employees: {
    type: Array,
    default: () => [],
  },
  activeEmployeesWithNoaccess: {
    type: Number,
    default: 0,
  },
  employeesDescription: {
    type: String,
    default: "",
  },
  employeesActionLabel: {
    type: String,
    default: "Apply Filter",
  },
  // Positions tab props
  positions: {
    type: Array,
    default: () => [],
  },
  positionsPagination: {
    type: Object,
    default: () => ({
      page: 1,
      total: 0,
      perPage: 10,
    }),
  },
  positionsLoading: {
    type: Boolean,
    default: false,
  },
  positionsAppliedFilters: {
    type: Array,
    default: () => [],
  },
  positionsSearchText: {
    type: String,
    default: "",
  },
  positionsBackendPagination: {
    type: Boolean,
    default: false,
  },
  positionsTitle: {
    type: String,
    default: "Positions Organization",
  },
  positionsActionLabel: {
    type: String,
    default: "Add Position",
  },
  positionsHideTopSection: {
    type: Boolean,
    default: true,
  },
  positionsHideActiveNo: {
    type: Boolean,
    default: false,
  },
  positionsActiveRecordsCount: {
    type: Number,
    default: null,
  },
  // Responsibilities tab props
  responsibilitiesResCount: {
    type: Number,
    default: 0,
  },
  responsibilities: {
    type: Array,
    default: () => [],
  },
  allResponsibilities: {
    type: Array,
    default: () => [],
  },
  responsibilitiesPagination: {
    type: Object,
    default: () => ({
      page: 1,
      total: 0,
      perPage: 10,
    }),
  },
  responsibilitiesLoading: {
    type: Boolean,
    default: false,
  },
  responsibilitiesChatterData: {
    type: Array,
    default: () => [],
  },
  responsibilitiesTotalChatterData: {
    type: Number,
    default: 0,
  },
  responsibilitiesSearchText: {
    type: String,
    default: "",
  },
  responsibilitiesAppliedFilters: {
    type: Array,
    default: () => [],
  },
  responsibilitiesFilterGroups: {
    type: Array,
    default: () => [],
  },
  responsibilitiesStatusOptions: {
    type: Array,
    default: () => ["draft", "active", "retired", "inactive"],
  },
  responsibilitiesShowSearchBar: {
    type: Boolean,
    default: true,
  },
  responsibilitiesTotalCount: {
    type: Number,
    default: 0,
  },
  // Counts for badges
  count_of_active_employees: {
    type: Number,
    default: 0,
  },
  count_of_active_positions: {
    type: Number,
    default: 0,
  },
  count_of_active_roles: {
    type: Number,
    default: 0,
  },
  count_of_active_responsibilities: {
    type: Number,
    default: 0,
  },
  count_of_active_procedures: {
    type: Number,
    default: 0,
  },
  // Initial tab
  initialTab: {
    type: String,
    default: "Dashboard",
  },
});

const emit = defineEmits(["guide-closed", "tab-changed"]);

const initializeDrilldownState = () => {
  // Only handle employee drilldown mode (roles, positions, responsibilities use standalone routes)
  const employeeIdFromQuery = route?.query?.employeeId;
  const employeeTabFromQuery = route?.query?.employeeTab;
  
  if (employeeIdFromQuery) {
    return {
      drilldown: {
        employeeId: employeeIdFromQuery,
        tab: employeeTabFromQuery || 'pathtoreadiness'
      },
      showContent: false,
      tab: "Employees"
    };
  }
  
  return {
    drilldown: null,
    showContent: true,
    tab: getTabFromRoute()
  };
};

const initialState = initializeDrilldownState();
const currentTab = ref(initialState.tab);
const showTabContent = ref(initialState.showContent);
const drilldownData = ref(initialState.drilldown);
const drilldownBreadcrumbs = ref(null); // Store breadcrumbs separately to avoid watcher issues

const authUser = ref(null);
const knockClient = ref(null);
const guideClient = ref(null);
const rolesGuideData = ref(null);
const dashboardGuideData = ref(null);
const guides = ref([]);

watch(
  () => route?.params?.tab,
  (newTab) => {
    if (newTab) {
      const matchedTab = validTabs.find(
        (tab) => tab.toLowerCase() === newTab.toLowerCase()
      );
      if (matchedTab) {
        currentTab.value = matchedTab;
      }
    } else {
      currentTab.value = "Dashboard";
    }
  }
);

const isLoadingEmployees = ref(false);

const loadEmployees = async () => {
  if (isLoadingEmployees.value) return;
  isLoadingEmployees.value = true;
  
  try {
    await employeesViewState.initializeFilters();
    
    const fromDashboard = route?.query?.fromDashboard === 'true';
    const searchEmployeeName = route?.query?.searchEmployee || '';
    
    if (fromDashboard) {
      await employeesViewState.removeAllFiltersAndReload();
      
      return;
    }
    
    employees.clearEmployeeDynamicFilters();
    employees.removeEmployeeFilter("first_name");
    employees.removeEmployeeFilter("last_name");
    employees.removeEmployeeFilter("name");
    employees.removeEmployeeFilter("status");
    employees.removeEmployeeFilter("archived");
    employees.removeEmployeeFilter("has_access");
    employees.removeEmployeeFilter("has_assignment");
    employees.setPaginationDataOffset(0);
    
    employeesViewState.loadFiltersOnReload();
    
    if (!employeesViewState.state.filters.values || employeesViewState.state.filters.values.length === 0) {
      employeesViewState.state.filters.values = ["draft", "active"];
      const filter = await import('@/store/pinia/filters');
      filter.default.setFilters({ 
        key: employeesViewState.state.filters.key, 
        filters: ["draft", "active"] 
      });
    }
    
    await proxy.$nextTick();
    
    const filtersToApply = employeesViewState.state.filters.values.map(f => {
      if (typeof f === 'string') return f;
      if (typeof f === 'object' && f.value) return f.value;
      return f;
    }).filter(Boolean);
    
    if (filtersToApply && filtersToApply.length > 0) {
      employeesViewState.state.filters.values = filtersToApply;
    }
    
    await proxy.$nextTick();
    
    if (filtersToApply && filtersToApply.length > 0) {
      await employeesViewState.applyFilters(filtersToApply);
    } else {
      await employees.loadAllEmployees();
    }
    
    await proxy.$nextTick();
    
    // Recalculate active employees with no access independently after loading employees
    await calculateActiveEmployeesWithNoAccess();
    
  } catch (error) {
    console.error("Error loading employees:", error);
  } finally {
    isLoadingEmployees.value = false;
  }
};

/**
 * Calculates the count of active employees with no access.
 * This calculation is INDEPENDENT of any filters applied to the employee list.
 * It fetches all active employees (status=active, archived=false) without filters
 * and counts those that don't have access.
 */
const calculateActiveEmployeesWithNoAccess = async () => {
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    if (!accountId) {
      activeEmployeesWithNoaccess.value = 0;
      return;
    }

    // Fetch all active employees independently, without any filters
    // Only filter by status=active and archived=false to get the true count
    let allActiveEmployees = [];
    let offset = 0;
    const limit = 1000;
    let hasMore = true;

    // Handle pagination to fetch all active employees
    while (hasMore) {
      const response = await getListofEmployeesV2({
        account_id: accountId,
        queryParamsObj: {
          status: "active",
          archived: false,
          limit: limit,
          offset: offset,
        },
      });

      if (response?.data?.results && response.data.results.length > 0) {
        allActiveEmployees = [...allActiveEmployees, ...response.data.results];
        offset += limit;
        // Check if there are more pages
        hasMore = response.data.results.length === limit && response.data.next;
      } else {
        hasMore = false;
      }
    }

    // Count active employees with no access
    // An employee has "no access" if:
    // 1. access is null/undefined, OR
    // 2. access.exists but access.has_access is false or undefined
    // Also ensure we only count employees with status === "active" as a safety measure
    const activeNoAccess = allActiveEmployees.filter(
      (emp) => emp.status === "active" && (!emp.access || emp.access.has_access !== true)
    );
    activeEmployeesWithNoaccess.value = activeNoAccess.length;
  } catch (error) {
    console.error("Error calculating active employees with no access:", error);
    activeEmployeesWithNoaccess.value = 0;
  }
};

watch(
  () => employees.allEmployees.value,
  (newVal) => {
    employeesData.value = newVal || [];
    // Don't recalculate based on filtered data - use independent calculation
  },
  { deep: true, immediate: true }
);

watch(
  () => currentTab.value,
  async (newTab) => {
    if (newTab === "Employees") {
      await loadEmployees();
      // Recalculate active employees with no access independently when switching to Employees tab
      await calculateActiveEmployeesWithNoAccess();
    }
  }
);

// Watch route.query object for URL-based navigation (back button, direct URL, etc.)
// NOTE: handleDrilldown() handles immediate updates from clicks, this is fallback for URL changes
// Watch the full query object to catch all changes reliably
watch(
  () => ({ 
    employeeId: route?.query?.employeeId, 
    employeeTab: route?.query?.employeeTab 
  }),
  (newQuery, oldQuery) => {
    const newEmployeeId = newQuery?.employeeId;
    const oldEmployeeId = oldQuery?.employeeId;
    const newEmployeeTab = newQuery?.employeeTab || 'pathtoreadiness';
    
    if (newEmployeeId) {
      const employeeIdStr = String(newEmployeeId);
      // Always update if employeeId changes or tab changes
      if (!drilldownData.value || 
          drilldownData.value.employeeId !== employeeIdStr || 
          drilldownData.value.tab !== newEmployeeTab) {
        drilldownData.value = {
          employeeId: employeeIdStr,
          tab: newEmployeeTab
        };
        showTabContent.value = false;
        currentTab.value = "Employees";
      }
    } else if (!newEmployeeId && oldEmployeeId) {
      // Only clear if we're actually removing the employeeId (back to list)
      drilldownData.value = null;
      drilldownBreadcrumbs.value = null;
      showTabContent.value = true;
    }
  },
  { immediate: true, deep: true }
);

const initializeKnock = async () => {
  try {
    const apiKey = configService.get("knockApiKey");
    const userId = authUser.value?.id?.toString();
    const channelId = configService.get("knockGuideChannelId");

    knockClient.value = new Knock(apiKey);
    knockClient.value.authenticate({ id: userId });

    guideClient.value = new KnockGuideClient(knockClient.value, channelId);

    guideClient.value.store.subscribe(() => {
      const storeState = guideClient.value.store.state;
      guides.value = guideClient.value.selectGuides(storeState) || [];
      
      const roleGuide = guides.value.find(
        (guide) => guide.key === "what-is-a-role"
      );
      rolesGuideData.value = roleGuide?.steps?.[0] || null;
      
      const dashboardGuide = guides.value.find(
        (guide) => guide.key === "welcome-as-account-admin"
      );
      dashboardGuideData.value = dashboardGuide?.steps?.[0] || null;
    });

    await guideClient.value.subscribe();
    await guideClient.value.fetch();
  } catch (error) {
    console.error("Error initializing Knock:", error);
  }
};

const handleGuideCloseForRoles = async () => {
  try {
    if (rolesGuideData.value && rolesGuideData.value.markAsArchived) {
      await rolesGuideData.value.markAsArchived();
      rolesGuideData.value = null;
    }
  } catch (error) {
    console.error("Error dismissing guide:", error);
  }
};

onMounted(async () => {
  authUser.value = JSON.parse(localStorage.getItem("user"));
  await initializeKnock();
  
  // Calculate active employees with no access independently on mount
  await calculateActiveEmployeesWithNoAccess();
  
  if (router && !route?.params?.tab && !drilldownData.value) {
    router.replace({ name: "NewOrganization", params: { tab: "dashboard" } });
  } else if (currentTab.value === "Employees") {
    await loadEmployees();
  }
});

const menuItems = computed(() => [
  {
    label: "Dashboard",
    icon: Home,
  },
  {
    label: "Employees",
    icon: Employee,
  },
  {
    label: "Positions",
    icon: Position,
  },
  {
    label: "Roles",
    icon: Role,
  },
  {
    label: "Responsibilities",
    icon: Responsibility,
  },
  {
    label: "Procedures",
    icon: Procedures,
  },
]);

const handleTabChange = (tabLabel) => {
  currentTab.value = tabLabel;
  
  showTabContent.value = true;
  drilldownData.value = null;
  
  if (router) {
    router.push({
      name: "NewOrganization",
      params: { tab: tabLabel.toLowerCase() },
    });
  }
  emit("tab-changed", tabLabel);
};

const handleGuideClose = () => {
  emit("guide-closed");
};

const handleDrilldown = (data) => {
  console.log('🟡 OrganizationPage.handleDrilldown() called', data);
  
  drilldownBreadcrumbs.value = data.breadcrumbs || null;
  
  // Only handle employeeId now (roles, positions, responsibilities use standalone routes)
  // FORCE IMMEDIATE UPDATE - Update state FIRST for instant reactivity (like positions/roles)
  if (data.employeeId) {
    console.log('🟡 OrganizationPage updating drilldownData', {
      employeeId: String(data.employeeId),
      tab: data.tab || 'pathtoreadiness'
    });
    
    drilldownData.value = {
      employeeId: String(data.employeeId),
      tab: data.tab || 'pathtoreadiness'
    };
    showTabContent.value = false;
    currentTab.value = "Employees";
    
    console.log('🟡 OrganizationPage state updated', {
      drilldownData: drilldownData.value,
      showTabContent: showTabContent.value,
      currentTab: currentTab.value
    });
    
    // Update route AFTER state update (non-blocking, for URL consistency)
    if (router) {
      nextTick(() => {
        const query = { employeeId: String(data.employeeId), employeeTab: data.tab || 'pathtoreadiness' };
        console.log('🟡 OrganizationPage navigating to route', {
          name: "NewOrganization",
          params: { tab: "employees" },
          query
        });
        
        router.push({
          name: "NewOrganization",
          params: { tab: "employees" },
          query
        }).catch((err) => {
          console.error('❌ OrganizationPage navigation error:', err);
        });
      });
    }
  } else {
    console.warn('⚠️ OrganizationPage.handleDrilldown: No employeeId in data', data);
  }
};

const handleBackToList = () => {
  showTabContent.value = true;
  drilldownData.value = null;
  drilldownBreadcrumbs.value = null;
  
  if (router) {
    const targetTab = currentTab.value.toLowerCase();
    if (route.name !== "NewOrganization" || route.params.tab !== targetTab || Object.keys(route.query).length > 0) {
      router.push({
        name: "NewOrganization",
        params: { tab: targetTab }
      }).catch(() => {});
    }
  }
};
</script>

<style scoped>
.organization-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 12px;
  margin-bottom: 12px;
}

.tab-content {
  flex: 1;
  width: 100%;
}

.organization-page-title-container {
  padding: 24px 0px 0px 0px;
}

.drilldown-content {
  flex: 1;
  width: 100%;
}
.responsibilities-tab-container {
  padding-bottom: 60px !important;
}
.role-tab-container {
  padding-bottom: 80px !important;
}
.positions-tab-container {
  padding-bottom: 100px !important;
}
.employees-tab-container {
  padding-bottom: 80px !important;
}
</style>
